import { useEffect, useRef, useState } from "react";
import { Icon } from "../../icons/Icon";
import { Carregando, Aviso } from "../../components/Layout";
import { api, fmt, type ErroApi } from "../../lib/api";
import { PODE_ANALISAR, PODE_HOMOLOGAR, type Sessao } from "./Admin";

type Publicacao = {
  id: number; tipo: string; rotulo: string; geradaEm: string;
  publicadaEm: string | null; publicadaPor: string | null; totalInscricoes: number;
};

type Cargo = { id: number; codigo: string; nome: string; componentes: Array<{ id: number; nome: string }> };

type LinhaClassificacao = {
  posicao: number; nome: string; pontosTotal: number; pontosQualificacao: number;
  pontosExperiencia: number; dataNascimento: string; origemCota: string | null; revertida: boolean;
};

type ListaPublicada = {
  lista: string; listaRotulo: string; cargo: string; componente: string | null;
  publicacao: string; publicadaEm: string | null; total: number; linhas: LinhaClassificacao[];
};

/**
 * Motor de classificação: gerar, conferir, homologar.
 *
 * A separação entre gerar e publicar é o ponto central desta tela. Gerar é
 * reversível e não aparece para ninguém de fora; publicar é o ato de
 * homologação da Comissão (itens 11.2 e 12.2) e, a partir dele, o candidato vê
 * a própria posição. Por isso só Presidente e Administrador publicam.
 */
export function Classificacao({ sessao }: { sessao: Sessao }) {
  const [publicacoes, setPublicacoes] = useState<Publicacao[] | null>(null);
  const [cargos, setCargos] = useState<Cargo[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const [exportPub, setExportPub] = useState("");
  const [exportCargo, setExportCargo] = useState("");
  const [exportComp, setExportComp] = useState("");
  const [exportLista, setExportLista] = useState("GERAL");
  const [confirmando, setConfirmando] = useState<Publicacao | null>(null);
  const [removendo, setRemovendo] = useState<Publicacao | null>(null);
  const modalRef = useRef<HTMLDialogElement>(null);
  const modalRemoverRef = useRef<HTMLDialogElement>(null);

  const [visualizando, setVisualizando] = useState<Publicacao | null>(null);
  const [linhas, setLinhas] = useState<ListaPublicada | null>(null);
  const [carregandoLinhas, setCarregandoLinhas] = useState(false);

  const podeGerar = PODE_ANALISAR.includes(sessao.perfil ?? "");
  const podePublicar = PODE_HOMOLOGAR.includes(sessao.perfil ?? "");

  async function carregar() {
    try {
      const [pubs, edital] = await Promise.all([
        api.get<Publicacao[]>("/api/admin/classificacao"),
        api.get<{ cargos: Cargo[] }>("/api/publico/edital"),
      ]);
      setPublicacoes(pubs);
      setCargos(edital.cargos);
      if (pubs.length && !exportPub) setExportPub(String(pubs[0].id));
      if (edital.cargos.length && !exportCargo) {
        setExportCargo(String(edital.cargos[0].id));
        const c = edital.cargos[0];
        if (c.componentes.length) setExportComp(String(c.componentes[0].id));
      }
    } catch {
      setPublicacoes([]);
    }
  }

  useEffect(() => { void carregar(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (visualizando) void visualizar(visualizando);
    /* eslint-disable-next-line */
  }, [exportCargo, exportComp, exportLista]);

  async function gerar(tipo: "PRELIMINAR" | "FINAL") {
    setOcupado(true); setErro(null); setAviso(null);
    try {
      const r = await api.post<{ aviso: string; totalInscricoes: number }>(
        `/api/admin/classificacao/gerar?tipo=${tipo}`,
      );
      setAviso(`${r.aviso} ${r.totalInscricoes} inscrição(ões) classificada(s).`);
      await carregar();
    } catch (e) { setErro((e as ErroApi).mensagem); }
    finally { setOcupado(false); }
  }

  async function publicar(id: number) {
    setOcupado(true); setErro(null); setAviso(null);
    try {
      await api.post(`/api/admin/classificacao/${id}/publicar`);
      setAviso("Classificação homologada. Ela já está visível para os candidatos.");
      await carregar();
    } catch (e) { setErro((e as ErroApi).mensagem); }
    finally { setOcupado(false); setConfirmando(null); modalRef.current?.close(); }
  }

  function pedirConfirmacao(p: Publicacao) {
    setConfirmando(p);
    modalRef.current?.showModal();
  }

  async function despublicar(id: number) {
    setOcupado(true); setErro(null); setAviso(null);
    try {
      await api.post(`/api/admin/classificacao/${id}/despublicar`);
      setAviso("Homologação removida. A lista deixou de ser visível para os candidatos.");
      await carregar();
    } catch (e) { setErro((e as ErroApi).mensagem); }
    finally { setOcupado(false); setRemovendo(null); modalRemoverRef.current?.close(); }
  }

  function pedirRemocao(p: Publicacao) {
    setRemovendo(p);
    modalRemoverRef.current?.showModal();
  }

  async function visualizar(p: Publicacao) {
    setVisualizando(p);
    setExportPub(String(p.id));
    setLinhas(null);
    setCarregandoLinhas(true);
    try {
      const q = new URLSearchParams({ cargoId: exportCargo, lista: exportLista });
      if (exportComp) q.set("componenteId", exportComp);
      setLinhas(await api.get<ListaPublicada>(`/api/admin/classificacao/${p.id}/linhas?${q}`));
    } catch (e) { setErro((e as ErroApi).mensagem); }
    finally { setCarregandoLinhas(false); }
  }

  async function descartar(id: number) {
    setOcupado(true); setErro(null); setAviso(null);
    try {
      await api.delete(`/api/admin/classificacao/${id}`);
      setAviso("Rodada descartada.");
      await carregar();
    } catch (e) { setErro((e as ErroApi).mensagem); }
    finally { setOcupado(false); }
  }

  if (!publicacoes) return <Carregando texto="Carregando classificações" />;

  const cargoSelecionado = cargos.find((c) => String(c.id) === exportCargo);
  const urlExport = (formato: string) => {
    const q = new URLSearchParams({ cargoId: exportCargo, lista: exportLista, formato });
    if (exportComp) q.set("componenteId", exportComp);
    return `/api/admin/exportar/classificacao/${exportPub}?${q}`;
  };

  return (
    <div className="pilha pilha-6">
      <div>
        <h1>Classificação</h1>
        <p className="texto-secundario medida">
          O motor ordena por pontuação total, aplica os critérios de desempate e monta as
          quatro listas, com a alternância das reservas de vaga, por cargo e componente
          curricular.
        </p>
      </div>

      {erro && <Aviso tipo="erro" titulo="Não foi possível concluir">{erro}</Aviso>}
      {aviso && <Aviso tipo="sucesso">{aviso}</Aviso>}

      {podeGerar && (
        <div className="cartao linha linha-quebra">
          <button className="btn btn-primario" disabled={ocupado} onClick={() => gerar("PRELIMINAR")}>
            <Icon nome="lista" tamanho={18} /> Gerar classificação preliminar
          </button>
          <button className="btn btn-secundario" disabled={ocupado} onClick={() => gerar("FINAL")}>
            <Icon nome="selo" tamanho={18} /> Gerar resultado final
          </button>
          <p className="texto-sutil cresce">
            Gerar não publica nada. A lista só fica visível ao candidato após a homologação.
          </p>
        </div>
      )}

      <div className="painel">
        <div className="painel-cabecalho"><h2 className="h3">Rodadas de classificação</h2></div>
        {publicacoes.length === 0 ? (
          <p className="vazio">Nenhuma classificação gerada ainda.</p>
        ) : (
          <div className="tabela-rolagem">
            <table className="tabela">
              <caption className="so-leitor">Rodadas geradas e situação de homologação</caption>
              <thead>
                <tr>
                  <th scope="col">Rodada</th><th scope="col">Tipo</th>
                  <th scope="col" className="col-num">Inscrições</th>
                  <th scope="col">Gerada em</th><th scope="col">Situação</th>
                  <th scope="col"><span className="so-leitor">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {publicacoes.map((p) => (
                  <tr key={p.id}>
                    <td><strong>{p.rotulo}</strong></td>
                    <td className="texto-sutil">{p.tipo.toLowerCase()}</td>
                    <td className="col-num">{p.totalInscricoes}</td>
                    <td className="num">{fmt.dataHora(p.geradaEm)}</td>
                    <td>
                      {p.publicadaEm ? (
                        <>
                          <span className="situacao situacao-DEFERIDA">homologada</span>
                          <br />
                          <span className="texto-sutil num">{fmt.dataHora(p.publicadaEm)}</span>
                          <br />
                          <span className="texto-sutil">{p.publicadaPor}</span>
                        </>
                      ) : (
                        <span className="situacao situacao-EM_ANALISE">não publicada</span>
                      )}
                    </td>
                    <td>
                      <div className="linha linha-quebra" style={{ gap: "var(--space-2)" }}>
                        <button className="btn btn-sutil btn-pequeno" disabled={carregandoLinhas}
                                onClick={() => visualizar(p)}>
                          <Icon nome="busca" tamanho={14} /> Visualizar
                        </button>
                        {!p.publicadaEm && podePublicar && (
                          <>
                            <button className="btn btn-primario btn-pequeno" disabled={ocupado}
                                    onClick={() => pedirConfirmacao(p)}>
                              Homologar
                            </button>
                            <button className="btn btn-sutil btn-pequeno" disabled={ocupado}
                                    onClick={() => descartar(p.id)}>
                              Descartar
                            </button>
                          </>
                        )}
                        {p.publicadaEm && podePublicar && (
                          <button className="btn btn-perigo btn-pequeno" disabled={ocupado}
                                  onClick={() => pedirRemocao(p)}>
                            Remover homologação
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {publicacoes.length > 0 && (
        <div className="painel">
          <div className="painel-cabecalho"><h2 className="h3">Conferir e exportar listas</h2></div>
          <div className="painel-corpo pilha">
            <div className="filtros">
              <div className="campo">
                <label className="campo-rotulo" htmlFor="e-pub">Rodada</label>
                <select id="e-pub" className="selecao" value={exportPub}
                        onChange={(e) => {
                          setExportPub(e.target.value);
                          const p = publicacoes.find((x) => String(x.id) === e.target.value);
                          if (p && visualizando) void visualizar(p);
                        }}>
                  {publicacoes.map((p) => <option key={p.id} value={p.id}>{p.rotulo}</option>)}
                </select>
              </div>
              <div className="campo">
                <label className="campo-rotulo" htmlFor="e-cargo">Cargo</label>
                <select id="e-cargo" className="selecao" value={exportCargo}
                        onChange={(e) => {
                          setExportCargo(e.target.value);
                          const c = cargos.find((x) => String(x.id) === e.target.value);
                          setExportComp(c?.componentes.length ? String(c.componentes[0].id) : "");
                        }}>
                  {cargos.map((c) => <option key={c.id} value={c.id}>{c.codigo}</option>)}
                </select>
              </div>
              {cargoSelecionado && cargoSelecionado.componentes.length > 0 && (
                <div className="campo">
                  <label className="campo-rotulo" htmlFor="e-comp">Componente</label>
                  <select id="e-comp" className="selecao" value={exportComp}
                          onChange={(e) => setExportComp(e.target.value)}>
                    {cargoSelecionado.componentes.map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="campo">
                <label className="campo-rotulo" htmlFor="e-lista">Lista</label>
                <select id="e-lista" className="selecao" value={exportLista}
                        onChange={(e) => setExportLista(e.target.value)}>
                  <option value="GERAL">Classificação Geral</option>
                  <option value="AMPLA">Ampla concorrência</option>
                  <option value="PCD">Pessoas com Deficiência</option>
                  <option value="NEGRO">Candidatos negros</option>
                </select>
              </div>
            </div>

            {carregandoLinhas && <Carregando texto="Carregando a lista" />}

            {!carregandoLinhas && linhas && (
              <div className="tabela-rolagem">
                <table className="tabela">
                  <caption className="so-leitor">
                    {linhas.listaRotulo}, {linhas.cargo}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col" className="col-num">Posição</th>
                      <th scope="col">Nome</th>
                      <th scope="col" className="col-num">Total</th>
                      <th scope="col" className="col-num">Qualif.</th>
                      <th scope="col" className="col-num">Exper.</th>
                      <th scope="col">Nascimento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.linhas.map((l) => (
                      <tr key={l.posicao}>
                        <td className="col-num"><strong>{l.posicao}</strong></td>
                        <td>
                          {l.nome}
                          {l.origemCota && (
                            <span className={`etiqueta ${l.origemCota === "PCD" ? "etiqueta-pcd" : "etiqueta-negro"}`}
                                  style={{ marginLeft: "var(--space-2)" }}>
                              {l.origemCota === "PCD" ? "PcD" : "negro"}
                            </span>
                          )}
                          {l.revertida && (
                            <span className="etiqueta" style={{ marginLeft: "var(--space-2)" }}>revertida</span>
                          )}
                        </td>
                        <td className="col-num"><strong>{fmt.pontos(l.pontosTotal)}</strong></td>
                        <td className="col-num">{fmt.pontos(l.pontosQualificacao)}</td>
                        <td className="col-num">{fmt.pontos(l.pontosExperiencia)}</td>
                        <td className="num">{l.dataNascimento}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {linhas.linhas.length === 0 && <p className="vazio">Nenhum candidato nesta lista.</p>}
              </div>
            )}

            <div className="linha linha-quebra">
              <a className="btn btn-secundario btn-pequeno" href={urlExport("csv")}>
                <Icon nome="planilha" tamanho={16} /> CSV
              </a>
              <a className="btn btn-secundario btn-pequeno" href={urlExport("xlsx")}>
                <Icon nome="planilha" tamanho={16} /> XLSX
              </a>
              <a className="btn btn-secundario btn-pequeno" href={urlExport("pdf")}>
                <Icon nome="arquivo" tamanho={16} /> PDF
              </a>
            </div>
          </div>
        </div>
      )}

      <dialog ref={modalRef} className="modal" onClose={() => setConfirmando(null)}>
        <div className="modal-cabecalho"><h2 className="h3">Homologar esta rodada?</h2></div>
        <div className="modal-corpo">
          <p>
            A partir da homologação, <strong>{confirmando?.rotulo}</strong> fica visível
            publicamente para os candidatos na página de classificação. A homologação pode
            ser removida depois, caso necessário.
          </p>
        </div>
        <div className="modal-acoes">
          <button type="button" className="btn btn-secundario" onClick={() => modalRef.current?.close()}>
            Cancelar
          </button>
          <button type="button" className="btn btn-primario" disabled={ocupado}
                  onClick={() => confirmando && publicar(confirmando.id)}>
            Homologar
          </button>
        </div>
      </dialog>

      <dialog ref={modalRemoverRef} className="modal" onClose={() => setRemovendo(null)}>
        <div className="modal-cabecalho"><h2 className="h3">Remover a homologação?</h2></div>
        <div className="modal-corpo">
          <p>
            <strong>{removendo?.rotulo}</strong> deixa de ficar visível para os candidatos. As
            inscrições que tinham passado para "classificada" por esta rodada voltam para
            "deferida" até uma nova homologação.
          </p>
        </div>
        <div className="modal-acoes">
          <button type="button" className="btn btn-secundario" onClick={() => modalRemoverRef.current?.close()}>
            Cancelar
          </button>
          <button type="button" className="btn btn-perigo" disabled={ocupado}
                  onClick={() => removendo && despublicar(removendo.id)}>
            Remover homologação
          </button>
        </div>
      </dialog>
    </div>
  );
}
